import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  selectTherapistProfile,
  selectClinicProfile,
} from "../../store/slices/authSlice";
import {
  useGetTherapistTicketsQuery,
  useCreateTicketMutation,
  useAddMessageMutation,
} from "../../store/services/supportApi";
import {
  STATUS_LABELS,
  STATUS_COLORS,
  formatTicketDate,
  getTicketStats,
  SUPPORT_TICKET_STATUSES,
} from "../../services/supportFirebase";
import { useToast } from "../../contexts/ToastContext";
import {
  Headset,
  WhatsappLogo,
  EnvelopeSimple,
  PaperPlaneTilt,
  Plus,
  ChatCircleDots,
  ArrowRight,
  CheckCircle,
  Clock,
  Warning,
  Info,
  Ticket,
  CaretLeft,
} from "@phosphor-icons/react";
import { HelpButton } from "../../components/shared";
import ContentLoader from "../../components/shared/ContentLoader";
import LoaderLoop from "../../components/shared/loader/LoaderLoop";
import "./SupportCenterPage.css";

// ============================================================================
// CONSTANTS
// ============================================================================

const WHATSAPP_NUMBER = "972543073737";
const SUPPORT_EMAIL = "office@clinikit.co.il";

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function SupportCenterPage() {
  const therapistProfile = useSelector(selectTherapistProfile);
  const clinicProfile = useSelector(selectClinicProfile);
  const { showSuccess, showError } = useToast();

  // State
  const [activeView, setActiveView] = useState("list"); // "list", "new", "chat"
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newTicketForm, setNewTicketForm] = useState({
    subject: "",
    description: "",
  });
  const [newMessage, setNewMessage] = useState("");

  const chatEndRef = useRef(null);

  // API
  const {
    data: tickets = [],
    isLoading: ticketsLoading,
    refetch: refetchTickets,
  } = useGetTherapistTicketsQuery(
    { therapistId: therapistProfile?.uid },
    { skip: !therapistProfile?.uid }
  );

  const [createTicket, { isLoading: creating }] = useCreateTicketMutation();
  const [addMessage, { isLoading: sending }] = useAddMessageMutation();

  // Scroll to bottom of chat when messages change
  useEffect(() => {
    if (activeView === "chat" && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [selectedTicket?.messages, activeView]);

  // Stats
  const stats = getTicketStats(tickets);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleWhatsAppClick = () => {
    const therapistName = `${therapistProfile?.firstName || ""} ${therapistProfile?.lastName || ""}`.trim();
    const message = encodeURIComponent(
      `שלום! אני ${therapistName || "מטפל/ת"} מ-CliniKit. אשמח לקבל עזרה.`
    );
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, "_blank");
  };

  const handleEmailClick = () => {
    window.location.href = `mailto:${SUPPORT_EMAIL}`;
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();

    if (!newTicketForm.subject.trim() || !newTicketForm.description.trim()) {
      showError("אנא מלא את כל השדות");
      return;
    }

    try {
      const therapistName = `${therapistProfile?.firstName || ""} ${therapistProfile?.lastName || ""}`.trim();

      await createTicket({
        clinicId: clinicProfile?.id,
        therapistId: therapistProfile?.uid,
        therapistName,
        clinicName: clinicProfile?.name,
        subject: newTicketForm.subject,
        description: newTicketForm.description,
      }).unwrap();

      showSuccess("הפנייה נשלחה בהצלחה!");
      setNewTicketForm({ subject: "", description: "" });
      setActiveView("list");
      refetchTickets();
    } catch (error) {
      console.error("Error creating ticket:", error);
      showError("שגיאה בשליחת הפנייה");
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!newMessage.trim() || !selectedTicket) return;

    try {
      const therapistName = `${therapistProfile?.firstName || ""} ${therapistProfile?.lastName || ""}`.trim();

      const updatedTicket = await addMessage({
        ticketId: selectedTicket.id,
        senderId: therapistProfile?.uid,
        senderName: therapistName,
        content: newMessage,
      }).unwrap();

      setNewMessage("");
      setSelectedTicket(updatedTicket);
      refetchTickets();
    } catch (error) {
      console.error("Error sending message:", error);
      showError("שגיאה בשליחת ההודעה");
    }
  };

  const handleSelectTicket = (ticket) => {
    setSelectedTicket(ticket);
    setActiveView("chat");
  };

  const handleBackToList = () => {
    setActiveView("list");
    setSelectedTicket(null);
  };

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const getStatusIcon = (status) => {
    switch (status) {
      case SUPPORT_TICKET_STATUSES.COMPLETED:
        return <CheckCircle size={14} weight="fill" />;
      case SUPPORT_TICKET_STATUSES.STUCK:
        return <Warning size={14} weight="fill" />;
      case SUPPORT_TICKET_STATUSES.WAITING_CUSTOMER:
        return <Info size={14} weight="fill" />;
      default:
        return <Clock size={14} weight="fill" />;
    }
  };

  // ============================================================================
  // RENDER: TICKETS LIST VIEW
  // ============================================================================

  const renderTicketsList = () => (
    <>
      {/* Quick Contact Cards */}
      <div className="sc-quick-contact">
        <button className="sc-contact-card sc-whatsapp" onClick={handleWhatsAppClick}>
          <div className="sc-contact-icon">
            <WhatsappLogo size={22} weight="fill" />
          </div>
          <div className="sc-contact-text">
            <span className="sc-contact-label">וואטסאפ</span>
            <span className="sc-contact-desc">מענה מהיר בצ׳אט</span>
          </div>
          <CaretLeft size={16} weight="bold" className="sc-contact-arrow" />
        </button>

        <button className="sc-contact-card sc-email" onClick={handleEmailClick}>
          <div className="sc-contact-icon">
            <EnvelopeSimple size={22} weight="fill" />
          </div>
          <div className="sc-contact-text">
            <span className="sc-contact-label">אימייל</span>
            <span className="sc-contact-desc">{SUPPORT_EMAIL}</span>
          </div>
          <CaretLeft size={16} weight="bold" className="sc-contact-arrow" />
        </button>
      </div>

      {/* Tickets Section */}
      <div className="sc-tickets-wrapper">
        <div className="sc-tickets-top">
          <div className="sc-tickets-title-row">
            <div className="sc-tickets-title">
              <Ticket size={20} weight="duotone" />
              <h2>הפניות שלי</h2>
            </div>
            <button
              className="sc-new-ticket-btn"
              onClick={() => setActiveView("new")}
            >
              <Plus size={16} weight="bold" />
              <span>פנייה חדשה</span>
            </button>
          </div>

          {/* Stats Row */}
          <div className="sc-stats-row">
            <div className="sc-stat-chip">
              <span className="sc-stat-num">{stats.total}</span>
              <span className="sc-stat-text">סה״כ</span>
            </div>
            <div className="sc-stat-chip sc-stat-open">
              <span className="sc-stat-num">{stats.open + stats.waitingAdmin + stats.inProgress}</span>
              <span className="sc-stat-text">פתוחות</span>
            </div>
            <div className="sc-stat-chip sc-stat-waiting">
              <span className="sc-stat-num">{stats.waitingCustomer}</span>
              <span className="sc-stat-text">ממתינות לך</span>
            </div>
            <div className="sc-stat-chip sc-stat-done">
              <span className="sc-stat-num">{stats.completed}</span>
              <span className="sc-stat-text">הסתיימו</span>
            </div>
          </div>
        </div>

        <div className="sc-privacy-bar">
          <Info size={14} />
          <span>הפניות שלך פרטיות ולא נראות למטפלים אחרים</span>
        </div>

        {/* Tickets List */}
        <div className="sc-tickets-body">
          {ticketsLoading ? (
            <ContentLoader fill message="טוען פניות..." />
          ) : tickets.length === 0 ? (
            <div className="sc-empty-state">
              <ChatCircleDots size={40} weight="duotone" />
              <h4>אין פניות עדיין</h4>
              <p>
                נתקלת בבעיה, יש לך שאלה או רעיון לשיפור?
                פתח פנייה חדשה ונחזור אליך בהקדם.
              </p>
            </div>
          ) : (
            <div className="sc-tickets-list">
              {tickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className={`sc-ticket-row ${
                    ticket.status === SUPPORT_TICKET_STATUSES.WAITING_CUSTOMER
                      ? "sc-ticket-alert"
                      : ""
                  }`}
                  onClick={() => handleSelectTicket(ticket)}
                >
                  <div className="sc-ticket-main">
                    <h4 className="sc-ticket-subject">{ticket.subject}</h4>
                    <div className="sc-ticket-meta">
                      <span>{formatTicketDate(ticket.createdAt)}</span>
                      <span className="sc-meta-dot">·</span>
                      <span>{ticket.messages?.length || 0} הודעות</span>
                    </div>
                    {ticket.messages?.length > 0 && (
                      <p className="sc-ticket-preview">
                        {ticket.messages[ticket.messages.length - 1]?.content?.substring(0, 80)}
                        {ticket.messages[ticket.messages.length - 1]?.content?.length > 80 ? "..." : ""}
                      </p>
                    )}
                  </div>
                  <div className="sc-ticket-side">
                    <span
                      className="sc-status-badge"
                      style={{
                        backgroundColor: `${STATUS_COLORS[ticket.status]}15`,
                        color: STATUS_COLORS[ticket.status],
                        borderColor: `${STATUS_COLORS[ticket.status]}30`,
                      }}
                    >
                      {getStatusIcon(ticket.status)}
                      {STATUS_LABELS[ticket.status]}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );

  // ============================================================================
  // RENDER: NEW TICKET FORM
  // ============================================================================

  const renderNewTicketForm = () => (
    <div className="sc-form-wrapper">
      <div className="sc-form-header">
        <button className="sc-back-btn" onClick={handleBackToList}>
          <ArrowRight size={18} weight="bold" />
          <span>חזרה</span>
        </button>
        <h2>פנייה חדשה</h2>
      </div>

      <form className="sc-form" onSubmit={handleCreateTicket}>
        <div className="sc-form-grid">
          <div className="sc-field sc-field-disabled">
            <label>שם מטפל/ת</label>
            <input
              type="text"
              value={`${therapistProfile?.firstName || ""} ${therapistProfile?.lastName || ""}`.trim()}
              disabled
            />
          </div>
          <div className="sc-field sc-field-disabled">
            <label>קליניקה</label>
            <input
              type="text"
              value={clinicProfile?.name || "לא צוין"}
              disabled
            />
          </div>
        </div>

        <div className="sc-field">
          <label>נושא הפנייה <span className="sc-required">*</span></label>
          <input
            type="text"
            value={newTicketForm.subject}
            onChange={(e) =>
              setNewTicketForm((prev) => ({ ...prev, subject: e.target.value }))
            }
            placeholder="לדוגמה: בעיה בקביעת תור"
            required
          />
        </div>

        <div className="sc-field">
          <label>תיאור <span className="sc-required">*</span></label>
          <textarea
            value={newTicketForm.description}
            onChange={(e) =>
              setNewTicketForm((prev) => ({
                ...prev,
                description: e.target.value,
              }))
            }
            placeholder="תאר את הבעיה או הבקשה..."
            rows={5}
            required
          />
        </div>

        <div className="sc-form-actions">
          <button
            type="button"
            className="sc-btn-ghost"
            onClick={handleBackToList}
          >
            ביטול
          </button>
          <button
            type="submit"
            className="sc-btn-primary"
            disabled={creating}
          >
            {creating ? (
              <>
                <LoaderLoop size="xs" />
                <span>שולח...</span>
              </>
            ) : (
              <>
                <PaperPlaneTilt size={16} weight="fill" />
                <span>שלח פנייה</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );

  // ============================================================================
  // RENDER: CHAT VIEW
  // ============================================================================

  const renderChatView = () => {
    if (!selectedTicket) return null;

    const isCompleted =
      selectedTicket.status === SUPPORT_TICKET_STATUSES.COMPLETED;

    return (
      <div className="sc-chat-wrapper">
        <div className="sc-chat-top">
          <button className="sc-back-btn" onClick={handleBackToList}>
            <ArrowRight size={18} weight="bold" />
            <span>חזרה</span>
          </button>
          <div className="sc-chat-title">
            <h2>{selectedTicket.subject}</h2>
            <span
              className="sc-status-badge"
              style={{
                backgroundColor: `${STATUS_COLORS[selectedTicket.status]}15`,
                color: STATUS_COLORS[selectedTicket.status],
                borderColor: `${STATUS_COLORS[selectedTicket.status]}30`,
              }}
            >
              {getStatusIcon(selectedTicket.status)}
              {STATUS_LABELS[selectedTicket.status]}
            </span>
          </div>
        </div>

        <div className="sc-chat-messages">
          {selectedTicket.messages?.map((msg) => (
            <div
              key={msg.id}
              className={`sc-msg ${
                msg.senderType === "therapist" ? "sc-msg-out" : "sc-msg-in"
              }`}
            >
              <div className="sc-msg-bubble">
                <div className="sc-msg-head">
                  <span className="sc-msg-name">{msg.senderName}</span>
                  <span className="sc-msg-time">
                    {formatTicketDate(msg.createdAt)}
                  </span>
                </div>
                <p>{msg.content}</p>
              </div>
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>

        {isCompleted ? (
          <div className="sc-chat-closed-bar">
            <CheckCircle size={18} weight="fill" />
            <span>הפנייה הסתיימה</span>
          </div>
        ) : (
          <form className="sc-chat-input-bar" onSubmit={handleSendMessage}>
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="הקלד הודעה..."
              rows={1}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage(e);
                }
              }}
            />
            <button
              type="submit"
              disabled={!newMessage.trim() || sending}
              className="sc-send-btn"
            >
              {sending ? (
                <LoaderLoop size="xs" />
              ) : (
                <PaperPlaneTilt size={18} weight="fill" />
              )}
            </button>
          </form>
        )}
      </div>
    );
  };

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <div className="sc-page">
      {/* Page Header */}
      <div className="sc-header">
        <div className="sc-header-content">
          <Headset size={28} weight="duotone" className="sc-header-icon" />
          <div>
            <h1>מרכז התמיכה</h1>
            <p>אנחנו כאן לעזור לך בכל שאלה או בעיה</p>
          </div>
        </div>
        <HelpButton featureKey="support_center" />
      </div>

      {/* Content */}
      <div className="sc-content">
        {activeView === "list" && renderTicketsList()}
        {activeView === "new" && renderNewTicketForm()}
        {activeView === "chat" && renderChatView()}
      </div>
    </div>
  );
}

export default SupportCenterPage;
